import { Controller } from "react-hook-form";

import { clsx, Input } from "@alpacahq/ui/old";

import type { Asset } from "~/v2/api/rest/account";
import type { Quote } from "~/v2/api/rest/data";
import { ORDER_TYPE_OPTIONS } from "~/v2/constants/equityOrder";
import { SelectWithSublabel } from "../../SelectWithSublabel";
import {
  BidAsk,
  BuySellButtons,
  orderTicketFrameClass,
  SymbolInfo,
} from "../shared";
import type { UseEquityOrderTicketControllerReturn } from "./EquityOrderTicket.controller";
import { CostInfo } from "./presentation/CostInfo";
import { EquityReviewCard } from "./presentation/EquityReviewCard";
import { ExtendedHoursButtons } from "./presentation/ExtendedHoursButtons";
import { QuantityInput } from "./presentation/QuantityInput";
import { TrailingValueInput } from "./presentation/TrailingValueInput";

interface EquityOrderTicketPresentationProps
  extends UseEquityOrderTicketControllerReturn {
  asset: Asset | undefined;
  quote: Quote | undefined;
  symbol: string;
  feedLabel?: string;
  onRefreshQuoteClicked: () => void;
  AssetSearch: JSX.Element;
  fill?: boolean;
}

export const EquityOrderTicketPresentation = ({
  // Form control & submission
  control,
  onSubmit,
  isSubmitDisabled,
  isSubmitLoading,
  isSubmitCompleted,
  // Review state
  isInReview,
  reviewSide,
  onConfirmOrder,
  onEditClicked,
  // Review quote (subscription-aware)
  reviewQuote,
  reviewQuoteLastUpdated,
  reviewFeedLabel,
  isReviewQuoteLoading,
  onRefreshReviewQuote,
  // Buying power
  buyingPower,
  // Price & quote
  midPrice,
  reviewMidPrice,
  priceChange,
  quoteLatestUpdated,
  estimatedCost,
  reviewEstimatedCost,
  // Order type
  orderType,
  onOrderTypeChange,
  // Quantity
  quantity,
  quantityType,
  onQuantityChange,
  onQuantityBlur,
  onQuantityTypeChange,
  isQuantityTypeDisabled,
  // Time in force
  timeInForce,
  onTimeInForceChange,
  timeInForceOptions,
  // Extended hours
  isExtendedHours,
  onIsExtendedHoursChange,
  isExtendedHoursDisabled,
  // Trailing stop
  trailType,
  trailValue,
  onTrailTypeChange,
  trailingStopPrice,
  // Available quantity
  sharesAvailable,
  dollarsAvailable,
  // Prices
  limitPrice,
  stopPrice,
  // Additional props
  asset,
  quote,
  symbol,
  feedLabel,
  onRefreshQuoteClicked,
  AssetSearch,
  fill,
  // Overnight
  isOvernightNotSupported,
  isOvernightHalted,
  isNotTradable,
}: EquityOrderTicketPresentationProps) => {
  if (isInReview) {
    return (
      <div className={orderTicketFrameClass(fill)}>
        <EquityReviewCard
          side={reviewSide}
          symbol={symbol}
          quote={reviewQuote}
          feedLabel={reviewFeedLabel}
          lastUpdated={reviewQuoteLastUpdated}
          onRefreshQuoteClicked={onRefreshReviewQuote}
          isRefreshing={isReviewQuoteLoading}
          isQuoteLoading={isReviewQuoteLoading}
          orderType={orderType}
          quantity={quantity}
          limitPrice={limitPrice}
          stopPrice={stopPrice}
          trailType={trailType}
          trailValue={trailValue}
          trailingStopPrice={trailingStopPrice}
          timeInForce={timeInForce}
          midPrice={reviewMidPrice.display}
          estimatedCost={reviewEstimatedCost}
          buyingPower={buyingPower.display}
          buyingPowerLabel={buyingPower.label}
          isSubmitLoading={isSubmitLoading}
          isSubmitCompleted={isSubmitCompleted}
          onEditClicked={onEditClicked}
          onSubmitClicked={onConfirmOrder}
        />
      </div>
    );
  }

  return (
    <form
      className={orderTicketFrameClass(fill)}
      onSubmit={(event) => {
        event.preventDefault();
      }}
    >
      {AssetSearch}
      <div
        className={clsx(
          "flex max-h-0 flex-col overflow-hidden transition-all duration-300",
          asset && quote && "max-h-64 gap-3",
        )}
      >
        {asset && (
          <SymbolInfo
            symbol={asset.symbol}
            name={asset.name}
            price={midPrice.display}
            priceChange={priceChange}
            showOvernightNotSupported={isOvernightNotSupported}
            showOvernightHalted={isOvernightHalted}
            showNotTradable={isNotTradable}
          />
        )}
        {quote && (
          <BidAsk
            bid={quote.bid.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            ask={quote.ask.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            lastUpdated={quoteLatestUpdated ?? ""}
            feedLabel={feedLabel}
            onRefreshQuoteClicked={onRefreshQuoteClicked}
          />
        )}
      </div>

      <QuantityInput
        control={control}
        isQuantityTypeDisabled={isQuantityTypeDisabled}
        quantityType={quantityType}
        onQuantityTypeChange={onQuantityTypeChange}
        sharesAvailable={sharesAvailable}
        dollarsAvailable={dollarsAvailable}
        onQuantityChange={onQuantityChange}
        onQuantityBlur={onQuantityBlur}
      />

      <SelectWithSublabel
        label="Order Type"
        value={orderType}
        onChange={onOrderTypeChange}
        options={ORDER_TYPE_OPTIONS}
      />

      {orderType === "limit" && (
        <Controller
          name="limitPrice"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Limit Price"
              {...field}
              type="number"
              min="0"
              step="any"
              leftIcon={<span>$</span>}
              errorMsg={error?.message}
            />
          )}
        />
      )}
      {orderType === "stop" && (
        <Controller
          name="stopPrice"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Stop Price"
              {...field}
              type="number"
              min="0"
              step="any"
              leftIcon={<span>$</span>}
              errorMsg={error?.message}
            />
          )}
        />
      )}
      {orderType === "stop_limit" && (
        <div className="grid grid-cols-2 gap-2">
          <Controller
            name="stopPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label="Stop Price"
                {...field}
                type="number"
                min="0"
                step="any"
                leftIcon={<span>$</span>}
                errorMsg={error?.message}
              />
            )}
          />
          <Controller
            name="limitPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                label="Limit Price"
                {...field}
                type="number"
                min="0"
                step="any"
                leftIcon={<span>$</span>}
                errorMsg={error?.message}
              />
            )}
          />
        </div>
      )}
      {orderType === "trailing_stop" && (
        <TrailingValueInput
          control={control}
          trailType={trailType}
          onTrailTypeChange={onTrailTypeChange}
          trailingStopPrice={trailingStopPrice}
        />
      )}

      {!isExtendedHoursDisabled && (
        <ExtendedHoursButtons
          isExtendedHours={isExtendedHours}
          onChange={onIsExtendedHoursChange}
        />
      )}
      <SelectWithSublabel
        label="Time In Force"
        value={timeInForce}
        onChange={onTimeInForceChange}
        options={timeInForceOptions}
      />
      <CostInfo
        estimatedCost={estimatedCost}
        buyingPower={buyingPower.display}
        buyingPowerLabel={buyingPower.label}
      />
      <BuySellButtons
        onBuyClicked={onSubmit("buy")}
        onSellClicked={onSubmit("sell")}
        isDisabled={isSubmitDisabled}
        isLoading={isSubmitLoading}
        isSubmitted={isSubmitCompleted}
      />
    </form>
  );
};
